/**
 * Read qualifying spend and configuration from the cart section, then convert
 * store-currency thresholds before showing unclaimed rewards.
 * Monthly availability and reward counts come from the same configuration as
 * the cart selector. No cart mutations are made by this reminder.
 */
if (!customElements.get('giftbox-reminder')) {
  customElements.define(
    'giftbox-reminder',
    class GiftBoxReminder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.link = this.querySelector('[data-giftbox-reminder-link]');
        this.badge = this.querySelector('[data-giftbox-reminder-badge]');
        this.titleElement = this.querySelector('[data-giftbox-reminder-title]');
        this.detail = this.querySelector('[data-giftbox-reminder-detail]');
        this.refreshTimer = null;
        this.refreshing = false;
        this.refreshAgain = false;
        this.revision = (this.revision || 0) + 1;
        this.boundPageShow = (event) => {
          if (event.persisted) this.scheduleRefresh();
        };
        window.addEventListener('pageshow', this.boundPageShow);

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleRefresh());
        }
        this.scheduleRefresh();
      }

      disconnectedCallback() {
        if (typeof this.unsubscribe === 'function') this.unsubscribe();
        window.removeEventListener('pageshow', this.boundPageShow);
        window.clearTimeout(this.refreshTimer);
        this.controller?.abort();
        this.controller = null;
        this.revision += 1;
        this.initialized = false;
      }

      scheduleRefresh() {
        window.clearTimeout(this.refreshTimer);
        this.revision += 1;
        // Never leave an old bonus visible while availability is unverified.
        this.renderState(null);
        this.refreshTimer = window.setTimeout(() => {
          this.refreshTimer = null;
          this.refresh();
        }, 100);
      }

      async refresh() {
        if (this.refreshing) {
          this.refreshAgain = true;
          return;
        }
        this.refreshing = true;
        const revision = this.revision;
        const controller = new AbortController();
        this.controller = controller;
        try {
          const cartUrl = this.dataset.cartUrl || `${window.Shopify?.routes?.root || '/'}cart`;
          const response = await fetch(cartUrl, {
            headers: { Accept: 'text/html' },
            cache: 'no-store',
            signal: controller.signal,
          });
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const rewards = html.querySelector('promo-rewards');
          if (!rewards) return;
          const state = this.readRewardState(rewards.dataset);
          if (!state) return;
          if (revision !== this.revision || controller.signal.aborted) return;
          this.renderState(state);
        } catch (_error) {
          // Unknown availability keeps the badge hidden and the cart link usable.
        } finally {
          if (this.controller === controller) {
            this.controller = null;
            this.refreshing = false;
            if (this.refreshAgain) {
              this.refreshAgain = false;
              this.scheduleRefresh();
            }
          }
        }
      }

      readRewardState(data) {
        // Foreign-currency HTML intentionally reports earnedMinis=0 pending JS.
        // Its qualifying total already excludes gift cards and reward products.
        const numbers = [data.qualifyingTotalCents, data.thresholdCents, data.maxMinis,
          data.selectedMinis, data.monthlySelected];
        if (!['true', 'false'].includes(data.monthlyAvailable) ||
            numbers.some(value => value === undefined || value === '' ||
              !Number.isSafeInteger(Number(value)) || Number(value) < 0)) return null;
        const baseThreshold = Number(data.thresholdCents);
        const maxMinis = Number(data.maxMinis);
        if (baseThreshold <= 0 || maxMinis < 1 || maxMinis > 12) return null;
        const { currency, storeCurrency } = data;
        const shopifyCurrency = window.Shopify?.currency;
        if (!/^[A-Z]{3}$/.test(currency || '') || !/^[A-Z]{3}$/.test(storeCurrency || '') ||
            (shopifyCurrency?.active && shopifyCurrency.active !== currency)) return null;
        const rate = currency === storeCurrency ? 1
          : shopifyCurrency?.active === currency ? Number(shopifyCurrency.rate) : NaN;
        if (!Number.isFinite(rate) || rate <= 0) return null;
        try {
          const digits = new Intl.NumberFormat('en', {style: 'currency', currency})
            .resolvedOptions().maximumFractionDigits;
          const quantum = 10 ** Math.max(0, 2 - digits);
          const maximum = baseThreshold * maxMinis * rate;
          if (!Number.isSafeInteger(Math.ceil(maximum)) || maximum < quantum) return null;
          let earnedMinis = 0;
          for (let tier = 1; tier <= maxMinis; tier += 1) {
            const threshold = Math.ceil(baseThreshold * tier * rate / quantum - 1e-9) * quantum;
            if (Number(data.qualifyingTotalCents) < threshold) break;
            earnedMinis = tier;
          }
          return {
            available: data.monthlyAvailable === 'true' && Number(data.fixedVariantId) > 0,
            earnedMinis,
            selectedMinis: Number(data.selectedMinis),
            monthlySelected: Number(data.monthlySelected),
          };
        } catch (_error) {
          return null;
        }
      }

      renderState(state) {
        const unavailable = state?.available === false;
        const earned = state?.available === true && state.earnedMinis > 0;
        const unclaimed = earned
          ? Math.max(0, state.earnedMinis - state.selectedMinis) + (state.monthlySelected > 0 ? 0 : 1)
          : 0;
        const title = unavailable
          ? 'Gift Box unavailable'
          : unclaimed > 0
            ? `${unclaimed} free reward${unclaimed === 1 ? '' : 's'} ready`
            : earned ? 'Gift Box rewards selected' : 'Gift Box rewards';
        const detail = unavailable
          ? "This month's special is unavailable"
          : unclaimed > 0 ? 'Choose in your cart'
            : state ? 'Review in your cart' : 'Check availability in your cart';
        if (this.badge) {
          this.badge.textContent = String(unclaimed);
          this.badge.hidden = unclaimed === 0;
        }
        if (this.titleElement) this.titleElement.textContent = title;
        if (this.detail) this.detail.textContent = detail;
        if (this.link) {
          this.link.setAttribute('data-promo-attention', String(unclaimed > 0));
          this.link.setAttribute('title', title);
          this.link.setAttribute('aria-label', `${title}. ${detail}.`);
        }
      }
    }
  );
}
